import { StringContext } from "../../core/StringContext";
import { StringData } from "../../core/StringData";
import { StringModule } from "../../core/StringModule";
import { StringObject } from "../../objects/StringObject";
import { isCoarsePointer } from "../../utils/isCoarsePointer";
import { styleTxn } from "../../utils/style-txn";

type CursorTargetState = {
  prevX: number;
  prevY: number;
};

type CursorObjectDimensions = {
  width: number;
  height: number;
  halfWidth: number;
  halfHeight: number;
};

type CursorPrevSnapshot = {
  x: number;
  y: number;
  stepX: number;
  stepY: number;
};

type CursorPortal = {
  id: string;
  element: HTMLElement;
  content: HTMLElement | null;
  prev: CursorPrevSnapshot;
  hoverCount: number;
  showTimer: ReturnType<typeof setTimeout> | null;
  lerp: number | null;
};

const targetStates = new WeakMap<StringObject, CursorTargetState>();
const CURSOR_EPSILON = 0.0005;
const DEFAULT_CURSOR_ID = "default";
const CURSOR_PORTAL_SELECTOR = "[string-cursor],[data-string-cursor]";
const CURSOR_PORTAL_CONTENT_SELECTOR = "[string-cursor-content],[data-string-cursor-content]";
const MIN_FRAME_DELTA = 1 / 240;
const roundTo = (value: number, precision: number) => {
  const scale = Math.pow(10, precision);
  return Math.round(value * scale) / scale;
};

function getTargetState(object: StringObject): CursorTargetState {
  let state = targetStates.get(object);
  if (!state) {
    state = { prevX: Number.NaN, prevY: Number.NaN };
    targetStates.set(object, state);
  }
  return state;
}

/**
 * StringCursor Module
 *
 * Handles cursor tracking and hover states for StringTune objects.
 *
 * Safari Navigation Fix:
 * Safari has an issue where mouseleave events are not fired when navigation occurs
 * (especially with NuxtLink/router navigation). This module includes several
 * workarounds to ensure proper cleanup:
 *
 * 1. MutationObserver - detects when elements are removed from DOM
 * 2. beforeunload/pagehide - captures traditional navigation
 * 3. visibilitychange - captures modern SPA navigation
 * 4. DOM existence checks - prevents operations on removed elements
 */
export class StringCursor extends StringModule {
  private cursorPrev: CursorPrevSnapshot = {
    x: Number.NaN,
    y: Number.NaN,
    stepX: Number.NaN,
    stepY: Number.NaN,
  };
  private cursorPortals: Map<string, CursorPortal[]> = new Map();
  private hoveredObjects: Set<StringObject> = new Set();
  private globalListenersBound = false;
  private boundBeforeUnload = () => this.cleanupHoverTargets();
  private boundPageHide = () => this.cleanupHoverTargets();
  private boundVisibilityChange = () => {
    if (document.hidden) {
      this.cleanupHoverTargets();
    }
  };
  protected enabled = true;

  private lastFrameTime: number = 0;

  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "cursor";
    this.cssProperties = [
      { name: "--x", syntax: "<number>", initialValue: "0", inherits: true },
      { name: "--y", syntax: "<number>", initialValue: "0", inherits: true },
      { name: "--x-lerp", syntax: "<number>", initialValue: "0", inherits: true },
      { name: "--y-lerp", syntax: "<number>", initialValue: "0", inherits: true },
      { name: "--x-px", syntax: "<number>", initialValue: "0", inherits: true },
      { name: "--y-px", syntax: "<number>", initialValue: "0", inherits: true },
      { name: "--dx", syntax: "<number>", initialValue: "0", inherits: true },
      { name: "--dy", syntax: "<number>", initialValue: "0", inherits: true },
      { name: "--angle", syntax: "<number>", initialValue: "0", inherits: true },
      { name: "--angle-deg", syntax: "<number>", initialValue: "0", inherits: true },
    ];

    this.permissions.mobile.rebuild.height = false;
    this.permissions.mobile.rebuild.width = false;
    this.permissions.mobile.rebuild.scrollHeight = false;

    this.attributesToMap = [
      ...this.attributesToMap,
      {
        key: "target-disable",
        type: "boolean",
        fallback: this.settings["target-disable"],
      },
      {
        key: "target-style-disable",
        type: "boolean",
        fallback: this.settings["target-style-disable"],
      },
      {
        key: "cursor-target",
        type: "string",
        fallback: this.settings["cursor-target"] ?? DEFAULT_CURSOR_ID,
      },
      {
        key: "target-class",
        type: "string",
        fallback: this.settings["target-class"],
      },
      {
        key: "cursor-class",
        type: "string",
        fallback: this.settings["cursor-class"],
      },
      {
        key: "alignment",
        type: { type: "enum", values: ["start", "center", "end"] },
        fallback: this.settings["alignment"],
      },
      {
        key: "cursor-enter",
        type: { type: "enum", values: ["snap", "smooth"] },
        fallback: "snap",
      },
      {
        key: "cursor-leave",
        type: { type: "enum", values: ["snap", "smooth"] },
        fallback: "smooth",
      },
      {
        key: "cursor-leave-hold",
        type: "boolean",
        fallback: false,
      },
      {
        key: "lerp",
        type: "number",
        fallback: this.settings["lerp"],
        transform: (value: number) => {
          return this.tools.adaptiveLerp.process({
            value,
            inMin: 0.1,
            inMax: 1.0,
            outMin: 0.05,
            outMax: 0.65,
          });
        },
      },
      {
        key: "cursor-float",
        type: "number",
        fallback: 2,
      },
      {
        key: "cursor-vars",
        type: "string",
        fallback: "",
      },
    ];

    if (isCoarsePointer()) {
      this.enabled = false;
    }

    this.collectCursorPortals();
    if (this.enabled) {
      this.bindGlobalLifecycleListeners();
    }
  }

  initializeObject(
    globalId: number,
    object: StringObject,
    element: HTMLElement,
    attributes: Record<string, any>,
  ): void {
    super.initializeObject(globalId, object, element, attributes);
    object.setProperty("mouse-x", 0);
    object.setProperty("mouse-y", 0);
    object.setProperty("mouse-pixel-x", 0);
    object.setProperty("mouse-pixel-y", 0);
    object.setProperty("is-mouse-over", false);
    object.setProperty("is-mouse-move", false);
    object.setProperty("__cursor-vars", this.parseCursorVars(object.getProperty("cursor-vars")));

    const rect = element.getBoundingClientRect();
    object.setProperty("cached-width", rect.width || element.offsetWidth || 1);
    object.setProperty("cached-height", rect.height || element.offsetHeight || 1);
  }

  onResize(): void {
    super.onResize();

    this.objects.forEach((object) => {
      const rect = object.htmlElement.getBoundingClientRect();
      object.setProperty("cached-width", rect.width || object.htmlElement.offsetWidth || 1);
      object.setProperty("cached-height", rect.height || object.htmlElement.offsetHeight || 1);
    });
  }

  onMutate(data: StringData): void {
    if (!this.enabled) return;

    const now = performance.now();

    let dt = this.lastFrameTime ? (now - this.lastFrameTime) / 1000 : 0.016;
    this.lastFrameTime = now;
    if (dt > 0.1) dt = 0.1;
    if (dt < MIN_FRAME_DELTA) dt = MIN_FRAME_DELTA;

    const cursorX = this.data.cursor.targetX;
    const cursorY = this.data.cursor.targetY;

    this.objects.forEach((object) => {
      const isOver = object.getProperty<boolean>("is-mouse-over");
      const isDisabled = object.getProperty<boolean>("cursor-target-disable");
      const baseLerp = object.getProperty<number>("lerp") ?? 0.15;

      const frameLerp = this.getFrameAdjustedLerp(baseLerp, dt);

      const dimensions = this.getObjectDimensions(object);
      const { halfWidth, halfHeight, width, height } = dimensions;

      if (isOver && !isDisabled) {
        const { cx, cy } = this.centers.getCenter(object);
        const elementX = cursorX - (cx - halfWidth);
        const elementY = cursorY - (cy - halfHeight);

        let px = object.getProperty<number>("mouse-pixel-x") ?? 0;
        let py = object.getProperty<number>("mouse-pixel-y") ?? 0;

        const dx = px - elementX;
        const dy = py - elementY;
        const distSquared = dx * dx + dy * dy;

        if (distSquared > 0.0001) {
          const isMoving = object.getProperty<boolean>("is-mouse-move") ?? false;
          const enterMode = object.getProperty<string>("cursor-enter") ?? "snap";
          const alignment = object.getProperty<string>("alignment") ?? "center";
          if (!isMoving) {
            object.setProperty("is-mouse-move", true);
            if (enterMode === "snap") {
              object.setProperty("mouse-pixel-x", elementX);
              object.setProperty("mouse-pixel-y", elementY);
              object.setProperty("mouse-x", elementX);
              object.setProperty("mouse-y", elementY);
              px = elementX;
              py = elementY;
            } else {
              const visualX = object.getProperty<number>("mouse-x") ?? 0;
              const visualY = object.getProperty<number>("mouse-y") ?? 0;
              px = this.reverseOffset(alignment, visualX, width);
              py = this.reverseOffset(alignment, visualY, height);
              object.setProperty("mouse-pixel-x", px);
              object.setProperty("mouse-pixel-y", py);
            }
            this.events.emit(this.getObjectEventName(object, "cursor:start"), null);
          }

          const lerpedX = this.tools.lerp.process({
            from: px,
            to: elementX,
            progress: frameLerp,
          });
          const lerpedY = this.tools.lerp.process({
            from: py,
            to: elementY,
            progress: frameLerp,
          });

          const updatedX = px + lerpedX;
          const updatedY = py + lerpedY;
          const pixelChanged =
            Math.abs(updatedX - px) > CURSOR_EPSILON || Math.abs(updatedY - py) > CURSOR_EPSILON;

          object.setProperty("mouse-pixel-x", updatedX);
          object.setProperty("mouse-pixel-y", updatedY);

          const offsetX = this.calculateOffset(alignment, updatedX, width);
          const offsetY = this.calculateOffset(alignment, updatedY, height);

          object.setProperty("mouse-x", offsetX);
          object.setProperty("mouse-y", offsetY);

          const coordsChanged = this.setMouseCoordinates(
            object,
            offsetX,
            offsetY,
            updatedX,
            updatedY,
          );

          if (coordsChanged) {
            this.events.emit(this.getObjectEventName(object, "cursor:move"), {
              x: offsetX,
              y: offsetY,
            });
          }
          if (pixelChanged) {
            this.events.emit(this.getObjectEventName(object, "cursor:pixel"), {
              x: updatedX,
              y: updatedY,
            });
          }
        } else {
          object.setProperty("mouse-pixel-x", elementX);
          object.setProperty("mouse-pixel-y", elementY);
          if (object.getProperty<boolean>("is-mouse-move")) {
            object.setProperty("is-mouse-move", false);
            this.events.emit(this.getObjectEventName(object, "cursor:end"), null);
          }
          const alignment = object.getProperty<string>("alignment") ?? "center";
          const offsetX = this.calculateOffset(alignment, elementX, width);
          const offsetY = this.calculateOffset(alignment, elementY, height);
          this.setMouseCoordinates(object, offsetX, offsetY, elementX, elementY);
        }
      } else {
        const holdOnLeave = object.getProperty<boolean>("cursor-leave-hold") ?? false;
        if (holdOnLeave) {
          if (object.getProperty<boolean>("is-mouse-move")) {
            object.setProperty("is-mouse-move", false);
            this.events.emit(this.getObjectEventName(object, "cursor:end"), null);
          }
          return;
        }

        const leaveMode = object.getProperty<string>("cursor-leave") ?? "smooth";
        if (leaveMode === "snap") {
          object.setProperty("is-mouse-move", false);
          object.setProperty("mouse-x", 0);
          object.setProperty("mouse-y", 0);
          object.setProperty("mouse-pixel-x", 0);
          object.setProperty("mouse-pixel-y", 0);
          this.setMouseCoordinates(object, 0, 0, 0, 0);
          return;
        }

        const mouseX = object.getProperty<number>("mouse-x") ?? 0;
        const mouseY = object.getProperty<number>("mouse-y") ?? 0;
        if (mouseX !== 0 || mouseY !== 0) {
          object.setProperty("is-mouse-move", false);

          const targetX = this.calculateOffset("center", halfWidth, width);
          const targetY = this.calculateOffset("center", halfHeight, height);

          const newMouseX =
            mouseX +
            this.tools.lerp.process({
              from: mouseX,
              to: targetX,
              progress: frameLerp,
            });
          const newMouseY =
            mouseY +
            this.tools.lerp.process({
              from: mouseY,
              to: targetY,
              progress: frameLerp,
            });

          object.setProperty("mouse-x", newMouseX);
          object.setProperty("mouse-y", newMouseY);

          if (Math.abs(newMouseX) < 0.001 && Math.abs(newMouseY) < 0.001) {
            object.setProperty("mouse-x", 0);
            object.setProperty("mouse-y", 0);
            object.setProperty("mouse-pixel-x", 0);
            object.setProperty("mouse-pixel-y", 0);
            this.setMouseCoordinates(object, 0, 0, 0, 0);
          } else {
            this.setMouseCoordinates(object, newMouseX, newMouseY);
          }
        }
      }
    });

    if (this.cursorPortals.size > 0) {
      const { stepX, stepY, smoothedX, smoothedY } = this.data.cursor;
      const prev = this.cursorPrev;
      const cursorChanged =
        !Number.isFinite(prev.x) ||
        Math.abs(smoothedX - prev.x) > CURSOR_EPSILON ||
        Math.abs(smoothedY - prev.y) > CURSOR_EPSILON ||
        Math.abs(stepX - prev.stepX) > CURSOR_EPSILON ||
        Math.abs(stepY - prev.stepY) > CURSOR_EPSILON;

      if (cursorChanged) {
        this.events.emit("cursor", {
          stepX,
          stepY,
          x: smoothedX,
          y: smoothedY,
        });

        this.cursorPrev = { x: smoothedX, y: smoothedY, stepX, stepY };
      }

      const targetX = this.data.cursor.targetX;
      const targetY = this.data.cursor.targetY;

      this.cursorPortals.forEach((bucket) => {
        bucket.forEach((portal) => {
          this.updatePortalPosition(portal, targetX, targetY, dt);
        });
      });
    }
  }

  onObjectConnected(object: StringObject) {
    const element = object.htmlElement;
    this.centers.attach(object);

    object.setProperty("mouseleave", () => {
      this.onMouseLeave(object);
    });
    object.setProperty("mouseenter", () => {
      this.onMouseEnter(object);
    });

    object.setProperty("onEnterEvent", this.onEnterObject.bind(this));
    object.events.on("enter", object.getProperty<(object: StringObject) => void>("onEnterEvent"));
    object.setProperty("onLeaveEvent", this.onLeaveObject.bind(this));
    object.events.on("leave", object.getProperty<(object: StringObject) => void>("onLeaveEvent"));
  }

  getCursorClass(object: StringObject) {
    const value = object.getProperty<string>("cursor-class");
    return value != null && value.length > 0 ? value : null;
  }

  onMouseEnter(object: StringObject) {
    if (!document.contains(object.htmlElement)) {
      return;
    }

    object.setProperty("is-mouse-over", true);
    this.hoveredObjects.add(object);

    const cursorClass = this.getCursorClass(object);

    this.withPortalsForObject(object, (portal) => {
      if (cursorClass) {
        portal.element.classList.add(cursorClass);
      }
      this.incrementPortalHover(portal);
    });

    object.htmlElement.addEventListener("mouseleave", object.getProperty("mouseleave"));
  }

  onMouseLeave(object: StringObject) {
    object.setProperty("is-mouse-over", false);
    this.hoveredObjects.delete(object);

    const cursorClass = this.getCursorClass(object);

    this.withPortalsForObject(object, (portal) => {
      if (cursorClass) {
        portal.element.classList.remove(cursorClass);
      }
      this.decrementPortalHover(portal);
    });

    if (document.contains(object.htmlElement)) {
      object.htmlElement.removeEventListener("mouseleave", object.getProperty("mouseleave"));
    }
  }

  private onEnterObject(object: StringObject) {
    object.htmlElement.addEventListener("mouseenter", object.getProperty("mouseenter"));
  }

  private onLeaveObject(object: StringObject) {
    object.htmlElement.removeEventListener("mouseenter", object.getProperty("mouseenter"));
    object.htmlElement.removeEventListener("mouseleave", object.getProperty("mouseleave"));
  }

  private safariNavigationCleanup(object: StringObject): void {
    if (object.getProperty<boolean>("is-mouse-over")) {
      this.onMouseLeave(object);
    }
  }

  private onElementRemovedFromDOM(object: StringObject): void {
    if (object.getProperty<boolean>("is-mouse-over")) {
      this.onMouseLeave(object);
    }
  }

  onObjectDisconnected(object: StringObject): void {
    if (object.getProperty<boolean>("is-mouse-over")) {
      this.onMouseLeave(object);
    }
  }

  onDOMRebuild(): void {
    if (!this.enabled) return;
    this.collectCursorPortals();
  }

  onDOMMutate(added: NodeList, removed: NodeList): void {
    if (!this.enabled) return;
    if (this.shouldRefreshPortals(added) || this.shouldRefreshPortals(removed)) {
      this.collectCursorPortals();
    }
    if (removed.length > 0) {
      this.handleRemovedNodes(removed);
    }
  }

  private collectCursorPortals(): void {
    this.cursorPortals.clear();
    const nodes = document.querySelectorAll(CURSOR_PORTAL_SELECTOR);
    nodes.forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      const id = this.resolvePortalId(node);
      const customLerp = this.resolvePortalLerp(node);
      const content = node.matches(CURSOR_PORTAL_CONTENT_SELECTOR)
        ? node
        : (node.querySelector(CURSOR_PORTAL_CONTENT_SELECTOR) as HTMLElement | null);

      const targetX = this.data.cursor.targetX;
      const targetY = this.data.cursor.targetY;
      const portal: CursorPortal = {
        id,
        element: node,
        content,
        prev: { x: targetX, y: targetY, stepX: 0, stepY: 0 },
        hoverCount: 0,
        showTimer: null,
        lerp: customLerp,
      };

      const existing = this.cursorPortals.get(id);
      if (existing) {
        existing.push(portal);
      } else {
        this.cursorPortals.set(id, [portal]);
      }
    });
  }

  private resolvePortalId(element: HTMLElement): string {
    const attrSources = [
      element.getAttribute("data-string-cursor"),
      element.getAttribute("string-cursor"),
      element.getAttribute("data-string-cursor-id"),
      element.getAttribute("string-cursor-id"),
    ];

    for (const source of attrSources) {
      if (source && source.trim().length > 0) {
        return source.trim();
      }
    }

    return DEFAULT_CURSOR_ID;
  }

  private resolvePortalLerp(element: HTMLElement): number | null {
    const raw =
      element.getAttribute("data-string-cursor-lerp") ??
      element.getAttribute("string-cursor-lerp") ??
      this.settings["cursor-lerp"];
    if (!raw) {
      return null;
    }

    const value = parseFloat(raw);
    if (!Number.isFinite(value)) {
      return null;
    }

    const clamped = Math.min(1, Math.max(0.01, value));
    return this.tools.adaptiveLerp.process({
      value: clamped,
      inMin: 0.1,
      inMax: 1.0,
      outMin: 0.05,
      outMax: 0.65,
    });
  }

  private shouldRefreshPortals(nodes: NodeList): boolean {
    for (const node of Array.from(nodes)) {
      if (!(node instanceof Element)) continue;
      if (node.matches(CURSOR_PORTAL_SELECTOR)) {
        return true;
      }
      if (node.querySelector(CURSOR_PORTAL_SELECTOR)) {
        return true;
      }
    }
    return false;
  }

  private withPortalsForObject(
    object: StringObject | null,
    cb: (portal: CursorPortal) => void,
  ): void {
    const portals = this.getPortalsForObject(object);
    portals.forEach((portal) => cb(portal));
  }

  private getPortalsForObject(object: StringObject | null): CursorPortal[] {
    if (this.cursorPortals.size === 0) {
      return [];
    }

    const ids = this.extractPortalIds(object);
    const selected: CursorPortal[] = [];

    ids.forEach((id) => {
      if (id === "*") {
        this.cursorPortals.forEach((bucket) => {
          bucket.forEach((portal) => selected.push(portal));
        });
        return;
      }

      const normalized = id.length > 0 ? id : DEFAULT_CURSOR_ID;
      const portals = this.cursorPortals.get(normalized);
      if (portals) {
        portals.forEach((portal) => selected.push(portal));
      }
    });

    if (selected.length === 0) {
      const fallbackBucket =
        this.cursorPortals.get(DEFAULT_CURSOR_ID) ?? this.cursorPortals.values().next().value;
      if (fallbackBucket && fallbackBucket.length > 0) {
        fallbackBucket.forEach((portal: any) => selected.push(portal));
      }
    }

    return selected;
  }

  private extractPortalIds(object: StringObject | null): string[] {
    if (!object) return [DEFAULT_CURSOR_ID];
    const raw = object.getProperty<string>("cursor-target");
    if (typeof raw !== "string" || raw.trim().length === 0) {
      return [DEFAULT_CURSOR_ID];
    }

    return raw
      .split(/[,|]/)
      .map((value) => value.trim())
      .filter(Boolean);
  }

  private incrementPortalHover(portal: CursorPortal): void {
    portal.hoverCount++;
    portal.element.classList.remove("-show");
    this.restartPortalShowTimer(portal);
  }

  private decrementPortalHover(portal: CursorPortal): void {
    portal.hoverCount = Math.max(0, portal.hoverCount - 1);
    if (portal.hoverCount === 0) {
      this.clearPortalShowTimer(portal);
      portal.element.classList.remove("-show");
    }
  }

  private restartPortalShowTimer(portal: CursorPortal): void {
    this.clearPortalShowTimer(portal);
    if (!portal.element.isConnected) {
      portal.showTimer = null;
      return;
    }
    portal.element.classList.add("-show");
    portal.showTimer = null;
  }

  private clearPortalShowTimer(portal: CursorPortal): void {
    if (portal.showTimer) {
      clearTimeout(portal.showTimer);
      portal.showTimer = null;
    }
  }

  private updatePortalPosition(
    portal: CursorPortal,
    targetX: number,
    targetY: number,
    dt: number,
  ): void {
    if (!portal.element.isConnected) return;

    const prev = portal.prev;

    const currentX = Number.isFinite(prev.x) ? prev.x : targetX;
    const currentY = Number.isFinite(prev.y) ? prev.y : targetY;

    const factor = portal.lerp ?? 0.1;
    const fpsAdjustedLerp = this.getFrameAdjustedLerp(factor, dt);

    const stepX = (targetX - currentX) * fpsAdjustedLerp;
    const stepY = (targetY - currentY) * fpsAdjustedLerp;

    const safeDt = dt > 1e-4 ? dt : 1 / 60;
    const normalizedVelocityX = stepX / (safeDt * 60);
    const normalizedVelocityY = stepY / (safeDt * 60);

    if (Math.abs(stepX) < CURSOR_EPSILON && Math.abs(stepY) < CURSOR_EPSILON) {
      return;
    }

    const nextX = currentX + stepX;
    const nextY = currentY + stepY;

    this.writePortalVars(portal.element, {
      "--x": roundTo(nextX, 2),
      "--y": roundTo(nextY, 2),
      "--x-lerp": roundTo(normalizedVelocityX, 3),
      "--y-lerp": roundTo(normalizedVelocityY, 3),
    });

    prev.x = nextX;
    prev.y = nextY;
    prev.stepX = stepX;
    prev.stepY = stepY;
  }

  private handleRemovedNodes(nodes: NodeList): void {
    if (this.hoveredObjects.size === 0) {
      return;
    }
    Array.from(this.hoveredObjects).forEach((object) => {
      if (!object.htmlElement.isConnected) {
        this.onElementRemovedFromDOM(object);
      }
    });
  }

  private cleanupHoverTargets(): void {
    if (this.hoveredObjects.size === 0) {
      return;
    }
    Array.from(this.hoveredObjects).forEach((object) => this.safariNavigationCleanup(object));
  }

  private bindGlobalLifecycleListeners(): void {
    if (this.globalListenersBound) {
      return;
    }
    window.addEventListener("beforeunload", this.boundBeforeUnload);
    window.addEventListener("pagehide", this.boundPageHide);
    document.addEventListener("visibilitychange", this.boundVisibilityChange);
    this.globalListenersBound = true;
  }

  private unbindGlobalLifecycleListeners(): void {
    if (!this.globalListenersBound) {
      return;
    }
    window.removeEventListener("beforeunload", this.boundBeforeUnload);
    window.removeEventListener("pagehide", this.boundPageHide);
    document.removeEventListener("visibilitychange", this.boundVisibilityChange);
    this.globalListenersBound = false;
  }

  private setMouseCoordinates(
    object: StringObject,
    x: number,
    y: number,
    pixelX?: number,
    pixelY?: number,
  ): boolean {
    if (object.getProperty("cursor-target-style-disable")) {
      return false;
    }

    const state = getTargetState(object);
    const floatNum = object.getProperty<number>("cursor-float") ?? 2;
    const scale = Math.pow(10, floatNum);
    const roundedX =
      Math.abs(x) < CURSOR_EPSILON &&
      Number.isFinite(state.prevX) &&
      Math.abs(state.prevX) < CURSOR_EPSILON
        ? state.prevX
        : Math.round(x * scale) / scale;
    const roundedY =
      Math.abs(y) < CURSOR_EPSILON &&
      Number.isFinite(state.prevY) &&
      Math.abs(state.prevY) < CURSOR_EPSILON
        ? state.prevY
        : Math.round(y * scale) / scale;

    if (
      Number.isFinite(state.prevX) &&
      Math.abs(roundedX - state.prevX) <= CURSOR_EPSILON &&
      Number.isFinite(state.prevY) &&
      Math.abs(roundedY - state.prevY) <= CURSOR_EPSILON
    ) {
      return false;
    }

    state.prevX = roundedX;
    state.prevY = roundedY;
    const vars = object.getProperty<Set<string>>("__cursor-vars");
    const extraVars: Record<string, number> = {
      "--x": roundTo(roundedX, floatNum),
      "--y": roundTo(roundedY, floatNum),
    };

    if (vars && vars.size > 0) {
      const px = Number.isFinite(pixelX)
        ? (pixelX as number)
        : object.getProperty<number>("mouse-pixel-x");
      const py = Number.isFinite(pixelY)
        ? (pixelY as number)
        : object.getProperty<number>("mouse-pixel-y");
      const prevPx = object.getProperty<number>("__prev-x-px");
      const prevPy = object.getProperty<number>("__prev-y-px");

      const dx = Number.isFinite(prevPx) ? px - prevPx : 0;
      const dy = Number.isFinite(prevPy) ? py - prevPy : 0;

      object.setProperty("__prev-x-px", px);
      object.setProperty("__prev-y-px", py);

      const angle = dx === 0 && dy === 0 ? 0 : Math.atan2(dy, dx);
      const angleDeg = (angle * 180) / Math.PI;

      if (vars.has("xpx")) extraVars["--x-px"] = roundTo(px, 2);
      if (vars.has("ypx")) extraVars["--y-px"] = roundTo(py, 2);
      if (vars.has("dx")) extraVars["--dx"] = roundTo(dx, 3);
      if (vars.has("dy")) extraVars["--dy"] = roundTo(dy, 3);
      if (vars.has("angle")) extraVars["--angle"] = roundTo(angle, 4);
      if (vars.has("angle-deg")) extraVars["--angle-deg"] = roundTo(angleDeg, 2);
    }

    const apply = () => {
      this.applyToElementAndConnects(object, (el) => {
        styleTxn.setVars(el, extraVars);
      });
    };
    if (styleTxn.isOpen) {
      apply();
    } else {
      styleTxn.run(apply);
    }

    return true;
  }

  private writePortalVars(el: HTMLElement, vars: Record<string, number>): void {
    if (styleTxn.isOpen) {
      styleTxn.setVars(el, vars);
      return;
    }
    styleTxn.run(() => {
      styleTxn.setVars(el, vars);
    });
  }

  private parseCursorVars(value: string | null | undefined): Set<string> {
    if (!value) return new Set();
    return new Set(
      value
        .split(/[|,]/)
        .map((v) => v.trim().toLowerCase())
        .filter((v) => v.length > 0),
    );
  }

  private getFrameAdjustedLerp(base: number, dt: number): number {
    const clampedBase = Math.min(0.99, Math.max(0.001, base));
    if (!Number.isFinite(dt) || dt <= 0) {
      return clampedBase;
    }

    const normalizedDt = Math.max(dt, MIN_FRAME_DELTA);
    const frames = normalizedDt * 60;
    const adjusted = 1 - Math.pow(1 - clampedBase, frames);
    return Math.min(0.999, Math.max(0.0001, adjusted));
  }

  private getObjectDimensions(object: StringObject): CursorObjectDimensions {
    const cachedWidth = object.getProperty<number>("cached-width");
    const cachedHeight = object.getProperty<number>("cached-height");

    if (
      typeof cachedWidth === "number" &&
      typeof cachedHeight === "number" &&
      cachedWidth > 0 &&
      cachedHeight > 0
    ) {
      const halfWidth = cachedWidth / 2;
      const halfHeight = cachedHeight / 2;
      return {
        width: cachedWidth,
        height: cachedHeight,
        halfWidth,
        halfHeight,
      };
    }

    const element = object.htmlElement;
    const fallbackWidth = element.offsetWidth || element.clientWidth || element.scrollWidth || 1;
    const fallbackHeight =
      element.offsetHeight || element.clientHeight || element.scrollHeight || 1;

    const rawHalfWidth = object.getProperty<number>("half-width");
    const rawHalfHeight = object.getProperty<number>("half-height");

    const halfWidth =
      typeof rawHalfWidth === "number" && Number.isFinite(rawHalfWidth)
        ? rawHalfWidth
        : fallbackWidth / 2;
    const halfHeight =
      typeof rawHalfHeight === "number" && Number.isFinite(rawHalfHeight)
        ? rawHalfHeight
        : fallbackHeight / 2;

    const width = halfWidth > 0 ? halfWidth * 2 : fallbackWidth;
    const height = halfHeight > 0 ? halfHeight * 2 : fallbackHeight;

    object.setProperty("cached-width", width);
    object.setProperty("cached-height", height);

    return { width, height, halfWidth, halfHeight };
  }

  private calculateOffset(alignment: string, mousePos: number, size: number): number {
    switch (alignment) {
      case "start":
        return mousePos / size;
      case "end":
        return (mousePos - size) / size;
      case "center":
      default:
        return (mousePos - size / 2) / (size / 2);
    }
  }

  private reverseOffset(alignment: string, offset: number, size: number): number {
    switch (alignment) {
      case "start":
        return offset * size;
      case "end":
        return offset * size + size;
      case "center":
      default:
        return offset * (size / 2) + size / 2;
    }
  }

  override removeObject(id: string): void {
    if (!this.enabled) return super.removeObject(id);
    const obj = this.objectMapOnPage.get(id);
    if (obj) {
      this.centers.detach(obj);
    }
    super.removeObject(id);
  }

  override destroy(): void {
    this.unbindGlobalLifecycleListeners();
    this.hoveredObjects.clear();
    super.destroy();
  }
}
